import React, {useState, useCallback} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import {useDropzone} from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import LinearProgress from "@mui/material/LinearProgress";
import * as Storage from "aws-amplify/storage";
import {remove} from "aws-amplify/storage";
import IconButton from "@mui/material/IconButton";
import awsconfig from "../../../awsconfig.js";
import Typography from "@mui/material/Typography";
import { getConfig } from 'pp-web';

export default function AdminCategoryDialog({email, setLoading, pageName}) {
    const config = getConfig(process.env.NEXT_PUBLIC_PROFILE);
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [path, setPath] = useState(null);
    const [hasSeenAmazonLink, setHasSeenAmazonLink] = useState(null);

    const handleOpenImportModal = () => {
        setOpen(true);
    };

    const handleCloseImportModal = () => {
        resetModalForm();
        setOpen(false);
    };

    React.useEffect(() => {
        const hasSeenAmazonLink = localStorage.getItem('hasSeenAmazonLink');
        if (hasSeenAmazonLink) {
            setHasSeenAmazonLink(true);
        } else {
            setHasSeenAmazonLink(false);
        }
    }, []);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: {xs: 330, md: 500},
        bgcolor: 'background.paper',
        // border: '1px solid #000',
        outline: 'none',
        boxShadow: 10,
        p: 2,
        borderRadius: 1,
    };

    const onDrop = useCallback(async (acceptedFiles) => {
        if (!(acceptedFiles.length > 0)) {
            console.log("No File Selected");
            return;
        }

        // Validate file names
        const acceptedFile = acceptedFiles[0];

        setIsUploading(true);

        const fileKey = await uploadImageToS3(acceptedFile, new Date().toJSON());

        setPath(fileKey);
        setFile(acceptedFile);
        setIsUploading(false);

    }, []);

    const onDropRejected = useCallback(() => {
        console.log("Only Excel .xlsx files are acceptable");
    }, []);

    const {getRootProps, getInputProps} = useDropzone({
        onDrop,
        onDropRejected,
        multiple: false,
        accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
    });
    const uploadImageToS3 = async (file, sId) => {
        try {
            setIsUploading(true);
            const result = await Storage.uploadData({
                path: `deal-categories/${sId}.xlsx`,
                data: file,
                options: {
                    onProgress: ({transferredBytes, totalBytes}) => {
                        if (totalBytes) {
                            const percentage = Math.round((transferredBytes / totalBytes) * 100);
                            setUploadProgress(percentage);
                        }
                    },
                    accessLevel: "guest",
                },
            }).result;
            return result.path;
        } catch (error) {
            console.log(error.message);
        }
    };

    const deleteFileFromS3 = async (filePath) => {
        try {
            return await remove({
                path: filePath,
                bucket: {
                    bucketName: awsconfig.Storage.S3.bucket,
                    region: awsconfig.Storage.S3.region,
                },
            });
        } catch (error) {
            console.log("Error ", error);
        }
    };

    const handleRemoveFile = async (path) => {
        try {
            const res = await deleteFileFromS3(path);
            if (res) {
                setFile(null);
                setPath(null);
            }
        } catch (error) {
            console.log("Error handling file removal:", error.message);
        }
    };

    async function handleUpload() {
        if (!file) {
            console.log("No file selected");
        } else {
            setLoading(true)
            try {
                let url = `${config.siteApi.apiUrl}/crm/categories`;

                await fetch(url, {
                    method: "PUT",
                    body: JSON.stringify({
                        s3FilePath: path,
                        email
                    })
                });
            } catch (e) {
                console.log('Error:', e);
            } finally {
                setLoading(false)
            }

            const cacheRefreshUrl = `${config.poln_web.app_base_url}/api/refresh?tags=site_categories_${pageName}`;
            await fetch(cacheRefreshUrl);

            typeof window !== 'undefined' && window.location.reload();
            handleCloseImportModal();
        }
    }

    async function handleDelete() {
        if (path && path.length > 0) {
            try {
                for (const item of path) {
                    await deleteFileFromS3(item);
                }
            } catch (e) {
                console.log("Error occur deleting file");
                handleCloseImportModal();
            }
        }
    }

    async function resetModalForm() {
        setPath(null);
        setFile(null);
    }


    const handleDownloadTemplate = async () => {
        setLoading(true)
        try {
            let url = `${config.siteApi.apiUrl}/crm/categories/template?email=${email}`;

            const response = await fetch(url);
            const data = await response.json();

            if (data.url) {
                const link = document.createElement("a");
                link.href = data.url;
                link.setAttribute("download", "Template.xlsx");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (e) {
            console.log('Error:', e);
        } finally {
            setLoading(false)
        }
    };

    if (hasSeenAmazonLink === null) {
        return null;
    }

    return (
        <Box display="flex" alignItems="center">
            <Button
                variant="contained"
                onClick={() => {
                    handleOpenImportModal()
                }}
                aria-describedby="category"
                size="small"
                sx={{
                    border: {
                        xs: '1px solid black',
                        md: 'none',
                    },
                    backgroundColor: {
                        xs: 'white',
                        // md: 'transparent',
                    },
                    color: {
                        xs: 'black',
                        // md: 'inherit',
                    },
                    '&:hover': {
                        backgroundColor: {
                            xs: '#f5f5f5',
                            // md: 'transparent',
                        },
                    },
                    height: 30
                }}
            >
                Update Categories
            </Button>
            {open && <Modal
                open={open}
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        handleCloseImportModal()
                    }
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description">
                <Box sx={style}>
                    <Typography id="modal-modal-title" variant="h5" component="h2" sx={{marginBottom: '20px'}}>
                        Update Deals Categories
                    </Typography>
                    <hr/>
                    <div>
                        <Box
                            {...getRootProps({className: "dropzone"})}
                            sx={{
                                border: "2px dashed #ffffff",
                                padding: "20px",
                                marginBottom: '20px',
                                textAlign: "center",
                                cursor: "pointer",
                                width: "auto",
                                color: "#ffffff",
                                background:'linear-gradient(to bottom, #1F1050, #857cc4)',
                            }}
                        >
                            <input {...getInputProps()} />
                            {isUploading || <>
                                <CloudUploadIcon sx={{fontSize: "48px", color: "white"}}/>
                                <p>Drag n drop an file here, or click to select a file</p>
                            </>}
                            {isUploading &&
                                <>
                                    <p>Uploading file ...</p>
                                    <LinearProgress
                                        variant="determinate"
                                        value={uploadProgress}
                                    />
                                    <small>{uploadProgress}% uploaded</small>
                                </>
                            }
                        </Box>
                        {
                            file && <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}>
                                <p>{file.name}</p>
                                <IconButton style={{cursor: "pointer"}} aria-label="remove"
                                            onClick={() => handleRemoveFile(path)}>
                                    <CloseIcon sx={{color: 'red'}}/>
                                </IconButton>
                            </Box>
                        }
                        {!hasSeenAmazonLink &&
                            <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Typography variant="h5" sx={{padding: 2, pt: 0}}>OR</Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleDownloadTemplate}
                                    style={{
                                            outline: "none",
                                            border: "none",
                                            backgroundColor: '#374046',
                                            color: "#fff",
                                            cursor: "pointer",
                                        padding: "10px 25px",
                                        fontSize: "16px",
                                        marginBottom: '15px'
                                        }}
                                >
                                    Download Template File
                                </Button>
                            </Box>
                        }
                    </div>
                    <div>
                        <hr/>
                        <Box display="flex" justifyContent="right" gap={2} padding={2}>
                            <Button variant="contained" sx={{background: '#EAD4C5', color: '#000000'}} size="small"
                                    onClick={() => {
                                        handleDelete()
                                        resetModalForm()
                                        handleCloseImportModal()
                                    }}
                            >
                                Cancel
                            </Button>
                            <Button variant="contained" color="success" size="small" onClick={handleUpload}
                                    disabled={!file}
                                    style={{
                                        outline: "none",
                                        border: "none",
                                        backgroundColor: file ? '#374046' : "#d3d3d3",
                                        color: file ? "#fff" : "#6c757d",
                                        cursor: file ? "pointer" : "not-allowed",
                                    }}
                            >
                                Proceed
                            </Button>
                        </Box>
                    </div>
                </Box>
            </Modal>}
        </Box>
    )
}
